import React, { useState } from 'react';
import { Marker } from '@react-google-maps/api';
import CompetitorInfoWindow from '../infoWindows/CompetitorInfoWindow';

/**
 * Builds a coloured SVG marker URL.
 * `type` is "gas_station" or "convenience_store".
 */
const getMarkerIcon = (type, rating) => {
  // Color by rating – green (good) to red (poor)
  let fill;
  if (rating == null) fill = '#9ca3af';       // grey – no rating
  else if (rating >= 4.0) fill = '#22c55e';   // green
  else if (rating >= 3.0) fill = '#eab308';   // yellow
  else if (rating >= 2.0) fill = '#f97316';   // orange
  else fill = '#ef4444';                      // red

  const isGas = type === 'gas_station';

  return {
    path: isGas
      ? 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z' // pin shape
      : 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
    fillColor: fill,
    fillOpacity: 0.9,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: isGas ? 1.5 : 1.3,
    anchor: { x: 12, y: 22 },
    labelOrigin: { x: 12, y: 9 },
  };
};

const CompetitorLayer = ({ gasStations = [], cStores = [], showGas = true, showCStores = true }) => {
  const [selected, setSelected] = useState(null);

  /**
   * Flatten radius-bucketed competitor objects into a single array.
   * The API returns { "1_mile": [...], "3_miles": [...], "5_miles": [...] }
   */
  const flatten = (bucket) => {
    if (Array.isArray(bucket)) return bucket;
    if (typeof bucket === 'object' && bucket !== null) {
      return Object.values(bucket).flat();
    }
    return [];
  };

  /** De-duplicate by place_id */
  const dedup = (arr) => {
    const seen = new Set();
    return arr.filter((p) => {
      if (!p.place_id || seen.has(p.place_id)) return false;
      seen.add(p.place_id);
      return true;
    });
  };

  const gasArr = showGas ? dedup(flatten(gasStations)) : [];
  const csArr = showCStores ? dedup(flatten(cStores)) : [];

  const renderMarkers = (places, type) =>
    places.map((place, idx) => {
      const lat = Number(place.latitude);
      const lng = Number(place.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      const position = { lat, lng };
      const icon = getMarkerIcon(type, place.rating);
      const label = type === 'gas_station' ? '⛽' : '🏪';

      return (
        <Marker
          key={place.place_id || `${type}-${idx}`}
          position={position}
          onClick={() => setSelected({ competitor: { ...place, type }, position })}
          icon={icon}
          label={{ text: label, fontSize: '12px' }}
          title={place.name}
        />
      );
    });

  return (
    <>
      {renderMarkers(gasArr, 'gas_station')}
      {renderMarkers(csArr, 'convenience_store')}

      {selected && (
        <CompetitorInfoWindow
          competitor={selected.competitor}
          position={selected.position}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default CompetitorLayer;
